import {
  GuideProgressItem,
  GuideProgressKind,
  GuideProgressStatus,
} from '../../types/guides';

export type GuideProgressMap = Record<string, GuideProgressItem>;

export const guideProgressKey = (
  kind: GuideProgressKind,
  itemId: string
): string => `${kind}:${itemId}`;

export const guideProgressStorageKey = (baseKey: string): string =>
  `${baseKey}:guides`;

const STATUS_RANK: Record<GuideProgressStatus, number> = {
  seen: 1,
  dismissed: 2,
  started: 3,
  completed: 4,
};

/** Newer wins; on equal timestamps the more advanced status wins so a completed tour never regresses. */
export const mergeGuideProgress = (
  ...sources: Array<GuideProgressItem[] | GuideProgressMap | null | undefined>
): GuideProgressMap => {
  const merged: GuideProgressMap = {};
  for (const source of sources) {
    if (!source) continue;
    const items = Array.isArray(source) ? source : Object.values(source);
    for (const item of items) {
      if (!item || !item.kind || !item.itemId) continue;
      const key = guideProgressKey(item.kind, item.itemId);
      const current = merged[key];
      if (!current) {
        merged[key] = item;
        continue;
      }
      const currentTime = Date.parse(current.updatedAt) || 0;
      const nextTime = Date.parse(item.updatedAt) || 0;
      if (
        nextTime > currentTime ||
        (nextTime === currentTime &&
          STATUS_RANK[item.status] > STATUS_RANK[current.status])
      ) {
        merged[key] = item;
      }
    }
  }
  return merged;
};

interface StoredProgress {
  [userLogin: string]: GuideProgressMap;
}

const readStore = (storageKey: string): StoredProgress => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as StoredProgress) : {};
  } catch {
    return {};
  }
};

export const readLocalGuideProgress = (
  storageKey: string,
  userLogin: string | null
): GuideProgressMap => {
  if (!userLogin) return {};
  const store = readStore(storageKey);
  const entry = store[userLogin];
  return entry && typeof entry === 'object' ? entry : {};
};

export const writeLocalGuideProgress = (
  storageKey: string,
  userLogin: string | null,
  progress: GuideProgressMap
): void => {
  if (!userLogin) return;
  try {
    const store = readStore(storageKey);
    store[userLogin] = progress;
    window.localStorage.setItem(storageKey, JSON.stringify(store));
  } catch {
    // Storage may be unavailable (private mode, quota); progress still lives on the server.
  }
};

export const applyGuideProgress = (
  progress: GuideProgressMap,
  update: {
    kind: GuideProgressKind;
    itemId: string;
    status: GuideProgressStatus;
    step?: number | null;
  }
): GuideProgressMap => ({
  ...progress,
  [guideProgressKey(update.kind, update.itemId)]: {
    kind: update.kind,
    itemId: update.itemId,
    status: update.status,
    step: update.step ?? null,
    updatedAt: new Date().toISOString(),
  },
});

export const getGuideProgress = (
  progress: GuideProgressMap,
  kind: GuideProgressKind,
  itemId: string
): GuideProgressItem | undefined => progress[guideProgressKey(kind, itemId)];
